import {
  CalendarIcon,
  DocumentArrowDownIcon,
  DocumentDuplicateIcon,
  HeartIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import React, { useState } from 'react'
import { FaUtensils } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import RecipeStructuredData from '../RecipeStructuredData'
import SocialSharing from '../SocialSharing'
import { Button, Card } from '../ui'
import CalendarDialog from '../ui/CalendarDialog'
import ImageCarousel from './ImageCarousel'

// Generate a very light pastel color with optional seed for deterministic generation
const getRandomPastelColor = (seed) => {
  // If seed is provided, use it to generate a deterministic color
  let r, g, b

  if (seed) {
    // Create a simple hash from the seed
    const hash = String(seed)
      .split('')
      .reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc)
      }, 0)

    // Use the hash to generate very light pastel RGB values
    const h = Math.abs(hash) % 360 // Hue (0-360)
    const s = 25 + (Math.abs(hash) % 20) // Lower saturation (25-45%)
    const l = 88 + (Math.abs(hash) % 7) // Higher lightness (88-95%)

    // Convert HSL to RGB
    const c = (1 - Math.abs(2 * (l / 100) - 1)) * (s / 100)
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l / 100 - c / 2

    if (h < 60) {
      ;[r, g, b] = [c, x, 0]
    } else if (h < 120) {
      ;[r, g, b] = [x, c, 0]
    } else if (h < 180) {
      ;[r, g, b] = [0, c, x]
    } else if (h < 240) {
      ;[r, g, b] = [0, x, c]
    } else if (h < 300) {
      ;[r, g, b] = [x, 0, c]
    } else {
      ;[r, g, b] = [c, 0, x]
    }

    r = Math.round((r + m) * 255)
      .toString(16)
      .padStart(2, '0')
    g = Math.round((g + m) * 255)
      .toString(16)
      .padStart(2, '0')
    b = Math.round((b + m) * 255)
      .toString(16)
      .padStart(2, '0')
  } else {
    // Random very light pastel color
    r = (Math.floor(Math.random() * 30) + 225).toString(16).padStart(2, '0')
    g = (Math.floor(Math.random() * 30) + 225).toString(16).padStart(2, '0')
    b = (Math.floor(Math.random() * 30) + 225).toString(16).padStart(2, '0')
  }

  return `${r}${g}${b}`
}

// Generate a data URL SVG with the given color
const getColorDataUrl = (colorHex) => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 600' width='800' height='600'%3E%3Crect width='800' height='600' fill='%23${colorHex}'/%3E%3C/svg%3E`
}

const RecipeCard = ({
  recipe,
  isSelected,
  isExpanded,
  favorites,
  ratings,
  ratingCounts,
  userRatings,
  user,
  dbUser,
  handleRecipeClick,
  handleToggleExpand,
  handleToggleFavorite,
  handleEdit,
  handleCopyRecipe,
  handleDelete,
  handleRating,
  deletingRecipeId,
  onAddToCalendar,
  onAddToIngredients,
}) => {
  const [showCalendarDialog, setShowCalendarDialog] = useState(false)
  const [starRotating, setStarRotating] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const isMobile = window.innerWidth < 768
  const navigate = useNavigate()

  // Get all recipe images if available, otherwise fall back to image_url
  const getRecipeImages = () => {
    if (recipe.recipe_images && recipe.recipe_images.length > 0) {
      // Sort images by display_order for proper prioritization, fallback to created_at
      const sortedImages = [...recipe.recipe_images].sort((a, b) => {
        // Primary sort by display_order (ascending - lower numbers first)
        if (a.display_order !== b.display_order) {
          return (a.display_order || 999) - (b.display_order || 999)
        }
        // Secondary sort by created_at (newest first) if display_order is the same
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      })

      return sortedImages.map((img) => img.image_url)
    }

    // Fallback to the image_url if recipe_images isn't available
    if (recipe.image_url) {
      return [recipe.image_url]
    }

    const bgColor = getRandomPastelColor(recipe.id)
    return [getColorDataUrl(bgColor)]
  }

  // Get the primary image for drag operations and fallbacks
  const getPrimaryImageUrl = () => {
    const images = getRecipeImages()
    return images[0]
  }

  const recipeImages = getRecipeImages()
  const primaryImageUrl = getPrimaryImageUrl()

  const handleCalendarClick = (e) => {
    e.stopPropagation()
    setShowCalendarDialog(true)
  }

  const handleStarClick = (e) => {
    e.stopPropagation()

    if (!favorites.has(recipe.id)) {
      setStarRotating(true)
    }

    handleToggleFavorite(recipe.id)

    // Reset rotation after animation completes
    setTimeout(() => {
      setStarRotating(false)
    }, 100) // Match duration with CSS transition
  }

  const handleDownloadPDF = async (e) => {
    e.stopPropagation()

    // Avoid multiple clicks
    if (isGeneratingPdf) return

    try {
      setIsGeneratingPdf(true)

      // Dynamically import the PDF generator to reduce initial load time
      const { generateRecipePDF } = await import('../../utils/pdfGenerator')

      // Validate recipe data before generating PDF
      if (!recipe) {
        throw new Error('Recipe data is missing')
      }

      // Generate PDF without user rating
      await generateRecipePDF(recipe)
    } catch (error) {
      console.error('PDF Generation Error:', error)

      // More detailed logging to help with debugging
      if (error instanceof TypeError) {
        console.error(
          'Type Error in PDF generation. Recipe data format may be incorrect:',
          {
            recipeId: recipe?.id,
            hasIngredients: Boolean(recipe?.ingredients),
            ingredientsType: recipe?.ingredients
              ? typeof recipe.ingredients
              : 'undefined',
            isIngredientsArray: recipe?.ingredients
              ? Array.isArray(recipe.ingredients)
              : false,
            hasInstructions: Boolean(recipe?.instructions),
            instructionsType: recipe?.instructions
              ? typeof recipe.instructions
              : 'undefined',
          }
        )
      }

      // If React Hot Toast is available in the application, show a toast notification
      if (typeof window !== 'undefined' && window.toast && window.toast.error) {
        window.toast.error('Failed to generate PDF. Please try again.')
      }
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleCardClick = (e) => {
    // Navigate to recipe detail page
    navigate(`/recipe/${recipe.id}`)
  }

  return (
    <>
      <Card
        id={`recipe-${recipe.id}`}
        isSelected={isSelected}
        isExpanded={isExpanded}
        onClick={handleCardClick}
        className={`${user && !isMobile ? 'cursor-grab' : 'cursor-pointer'} hover:shadow-sm transition-all duration-300 flex flex-col h-auto relative group ${isExpanded ? 'recipe-expanded' : ''}`}
        draggable={!!user && !isMobile}
        onDragStart={(e) => {
          if (!user || isMobile) return

          e.dataTransfer.setData(
            'recipe',
            JSON.stringify({
              id: recipe.id,
              name: recipe.name,
              image_url: primaryImageUrl,
            })
          )

          const dragImage = e.currentTarget.cloneNode(true)
          dragImage.style.width = e.currentTarget.offsetWidth * 0.75 + 'px'
          dragImage.style.height = e.currentTarget.offsetHeight * 0.75 + 'px'
          dragImage.style.position = 'absolute'
          dragImage.style.transform = 'scale(0.75)'

          // Remove the footer from the drag image
          const footer = dragImage.querySelector('.hover-trigger')
          if (footer) {
            footer.remove()
          }

          document.body.appendChild(dragImage)

          if (e.dataTransfer.setDragImage) {
            e.dataTransfer.setDragImage(dragImage, 0, 0)
          }

          e.currentTarget.dragImageElement = dragImage
        }}
        onDragEnd={(e) => {
          if (e.currentTarget.dragImageElement) {
            document.body.removeChild(e.currentTarget.dragImageElement)
            delete e.currentTarget.dragImageElement
          }
        }}
      >
        <RecipeStructuredData recipe={recipe} />
        <div className="flex flex-col h-full">
          <div className="relative w-full aspect-[16/9] h-full mb-2 overflow-hidden">
            {recipeImages.length > 0 ? (
              <ImageCarousel
                images={recipeImages}
                alt={recipe.name}
                className="absolute inset-0 w-full h-full rounded-t-lg"
              />
            ) : (
              <div className="w-full h-full rounded-t-lg flex items-center justify-center overflow-hidden">
                <img
                  src={getColorDataUrl(getRandomPastelColor(recipe.id))}
                  alt={recipe.name || 'Recipe'}
                  className="w-full h-full object-cover"
                  draggable="false"
                  onDragStart={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {recipe.users?.image_url && (
              <div className="absolute bottom-2 left-2 z-10">
                <img
                  src={recipe.users.image_url}
                  alt={`${recipe.users.first_name} ${recipe.users.last_name}`}
                  className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}

            {recipe.cooking_time_minutes && (
              <div className="absolute bottom-2 right-2 z-10">
                <div
                  className="bg-black bg-opacity-60 text-white text-xs font-medium px-2 py-1 rounded-md flex items-center"
                  draggable="false"
                  onDragStart={(e) => e.stopPropagation()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    draggable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {recipe.cooking_time_minutes} min
                </div>
              </div>
            )}
          </div>

          <div className="px-3 pb-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-gray-800 leading-tight min-h-[1.25rem]">
                {recipe.name}
              </h3>
              <div className="flex items-center flex-shrink-0">
                {user && (
                  <button
                    onClick={handleStarClick}
                    className={`transition-all duration-200 cursor-pointer p-1.5 rounded-full ${
                      favorites.has(recipe.id)
                        ? 'opacity-100 hover:bg-red-50'
                        : 'lg:opacity-0 lg:group-hover:opacity-100 lg:hover:opacity-100 opacity-70 hover:bg-gray-100'
                    }`}
                    title={
                      favorites.has(recipe.id)
                        ? 'Remove from favorites'
                        : 'Add to favorites'
                    }
                  >
                    {favorites.has(recipe.id) ? (
                      <HeartIconSolid
                        className={`h-5 w-5 text-red-500 transition-transform duration-300 ease-in-out ${starRotating ? 'rotate-72' : ''}`}
                      />
                    ) : (
                      <HeartIcon
                        className={`h-5 w-5 text-gray-400 hover:text-red-500 transition-transform duration-300 ease-in-out ${starRotating ? 'rotate-72' : ''}`}
                      />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="mt-auto px-3 pb-2 flex items-center justify-between">
            {ratings[recipe.id] ? (
              <div className="flex items-baseline gap-1">
                <span
                  className={`text-sm ${
                    ratings[recipe.id] >= 4.5
                      ? 'text-green-600 font-bold'
                      : ratings[recipe.id] >= 4.0
                        ? 'text-green-600 font-semibold'
                        : ratings[recipe.id] >= 3.5
                          ? 'text-green-600'
                          : ratings[recipe.id] >= 3.0
                            ? 'text-gray-500'
                            : 'text-gray-400'
                  }`}
                  style={{
                    opacity: 0.4 + (ratings[recipe.id] / 5) * 0.6,
                  }}
                >
                  {ratings[recipe.id].toFixed(1)}
                </span>
                <span
                  className="text-xs text-gray-300"
                  style={{ opacity: 0.4 + (ratings[recipe.id] / 5) * 0.6 }}
                >
                  ({ratingCounts[recipe.id] || 0})
                </span>
              </div>
            ) : (
              <div className="text-sm text-gray-400">No ratings</div>
            )}
            {user && (
              <div className="relative">
                {userRatings[recipe.id] ? (
                  <div
                    className="mt-1 mb-1 relative h-5 w-30 flex items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation()
                      const rect = e.currentTarget.getBoundingClientRect()
                      const x = e.clientX - rect.left
                      const width = rect.width
                      const rating = Math.ceil((x / width) * 5)
                      handleRating(recipe.id, rating)
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <FaUtensils
                        key={num}
                        className={`h-4 w-4 mx-0.5 ${
                          num <= userRatings[recipe.id]
                            ? 'text-yellow-500'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                ) : (
                  <div
                    className="mt-1 mb-1 relative h-5 w-30 flex items-center cursor-pointer lg:group-hover:flex active:flex"
                    onClick={(e) => {
                      e.stopPropagation()
                      const rect = e.currentTarget.getBoundingClientRect()
                      const x = e.clientX - rect.left
                      const width = rect.width
                      const rating = Math.ceil((x / width) * 5)
                      handleRating(recipe.id, rating)
                    }}
                    onTouchStart={(e) => {
                      // For touch devices, show the hover effect
                      const rect = e.currentTarget.getBoundingClientRect()
                      const touch = e.touches[0]
                      const x = touch.clientX - rect.left
                      const width = rect.width
                      const rating = Math.ceil((x / width) * 5)
                      handleRating(recipe.id, rating)
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <FaUtensils
                        key={num}
                        className="h-4 w-4 mx-0.5 text-gray-300 hover:text-yellow-500"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {isExpanded && <RecipeDetails recipe={recipe} />}

          <div className="mt-auto px-3 py-2 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between hover-trigger rounded-b-lg">
            <div className="flex-1">
              {user ? (
                <div className="ml-0 flex gap-1 sm:gap-2 transition-opacity">
                  <Button
                    variant="text"
                    size="xs sm:sm"
                    className="text-gray-600 hover:bg-green-50 hover:text-green-600 cursor-pointer p-1.5 rounded-full transition-all duration-200"
                    onClick={(e) => e.stopPropagation()}
                    title="Share recipe"
                  >
                    <SocialSharing recipe={recipe} recipeId={recipe.id} />
                  </Button>
                  <Button
                    variant="text"
                    size="xs sm:sm"
                    className="text-gray-600 hover:bg-gray-100 hover:text-blue-600 cursor-pointer p-1.5 rounded-full transition-all duration-200 lg:opacity-0 lg:group-hover:opacity-100"
                    onClick={async (e) => {
                      e.stopPropagation()
                      await handleCopyRecipe(recipe)
                    }}
                    title="Copy recipe"
                  >
                    <DocumentDuplicateIcon className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="text"
                    size="xs sm:sm"
                    className="text-gray-600 hover:bg-gray-100 hover:text-purple-600 cursor-pointer p-1.5 rounded-full transition-all duration-200 lg:opacity-0 lg:group-hover:opacity-100"
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPdf}
                    title="Download recipe PDF"
                  >
                    {isGeneratingPdf ? (
                      <svg
                        className="animate-spin h-5 w-5 text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <DocumentArrowDownIcon className="h-5 w-5" />
                    )}
                    {isGeneratingPdf && (
                      <span className="sr-only">Generating PDF...</span>
                    )}
                  </Button>
                  {recipe.users?.id === dbUser?.id && (
                    <Button
                      variant="text"
                      size="xs sm:sm"
                      className="text-gray-600 hover:bg-gray-100 hover:text-amber-500 cursor-pointer p-1.5 rounded-full transition-all duration-200 lg:opacity-0 lg:group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEdit(recipe)
                      }}
                      title="Edit recipe"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Button>
                  )}
                  {recipe.users?.id === dbUser?.id && (
                    <Button
                      variant="text"
                      size="xs sm:sm"
                      className="text-gray-600 hover:bg-red-50 hover:text-red-600 cursor-pointer p-1.5 rounded-full transition-all duration-200 lg:opacity-0 lg:group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(recipe.id)
                      }}
                      disabled={deletingRecipeId === recipe.id}
                      title={
                        deletingRecipeId === recipe.id
                          ? 'Deleting...'
                          : 'Delete recipe'
                      }
                    >
                      <TrashIcon className="h-5 w-5" />
                      {deletingRecipeId === recipe.id && (
                        <span className="sr-only">Deleting...</span>
                      )}
                    </Button>
                  )}
                  <div className="relative">
                    <Button
                      variant="text"
                      size="xs sm:sm"
                      className="lg:hidden text-gray-600 hover:bg-green-50 hover:text-green-600 cursor-pointer p-1.5 rounded-full transition-all duration-200"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleCalendarClick(e)
                      }}
                      title="Add to calendar"
                    >
                      <CalendarIcon className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Empty div to maintain layout when buttons are hidden */}
                </div>
              )}
            </div>
            <Button
              variant="text"
              size="sm"
              className="text-green-600 font-medium hover:bg-green-50 hover:text-green-700 cursor-pointer px-3 py-1.5 rounded-md transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation()
                handleRecipeClick(recipe.id)
              }}
            >
              Ingredients
            </Button>
          </div>
        </div>
      </Card>

      <CalendarDialog
        isOpen={showCalendarDialog}
        onClose={() => setShowCalendarDialog(false)}
        selectedRecipe={recipe}
        onAddToIngredients={(recipeIds) => {
          if (onAddToIngredients) {
            onAddToIngredients(recipeIds)
          }
          // Close the dialog when ingredients are added
          setShowCalendarDialog(false)
        }}
      />
    </>
  )
}

const RecipeDetails = ({ recipe }) => {
  return (
    <div className="p-4 space-y-6">
      {recipe.url && (
        <a
          href={recipe.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-green-600 hover:text-green-700 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
          View Recipe
        </a>
      )}
      <div className="flex max-sm:flex-col flex-wrap -mx-2 max-sm:gap-4">
        <div className="w-full sm:w-[30%] px-2 min-w-[150px]">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">
            Ingredients
          </h4>
          <div className="space-y-1 text-sm sm:text-base">
            {(() => {
              try {
                // First try to parse as JSON
                const parsedIngredients = JSON.parse(recipe.ingredients || '[]')
                return parsedIngredients.map((ingredient, index) => {
                  // Define units that should have no space
                  const noSpaceUnits = [
                    'g',
                    'kg',
                    'mL',
                    'L',
                    'tsp',
                    'tbsp',
                    'oz',
                    'lb',
                  ]
                  const hasNoSpace = noSpaceUnits.includes(ingredient.unit)

                  return (
                    <div key={index} className="flex items-center">
                      <span className="text-gray-800 mr-2 leading-none">•</span>
                      <span className="flex-1">
                        <span className="font-semibold">{ingredient.item}</span>
                        {ingredient.amount && (
                          <span className="text-gray-700 ml-1">
                            {ingredient.amount}
                            {ingredient.unit &&
                              (hasNoSpace
                                ? ingredient.unit
                                : ` ${ingredient.unit}`)}
                          </span>
                        )}
                      </span>
                    </div>
                  )
                })
              } catch (error) {
                // If JSON parsing fails, try to parse as string format
                try {
                  const ingredientString = recipe.ingredients || ''
                  // Remove the outer brackets
                  const cleanString = ingredientString.replace(/^\[|\]$/g, '')
                  // Split by "},{"
                  const ingredients = cleanString.split('},{').map((item) => {
                    // Clean up the item string and parse as JSON
                    const cleanItem = item.replace(/^{/, '{').replace(/}$/, '}')
                    return JSON.parse(cleanItem)
                  })

                  return ingredients.map((ingredient, index) => {
                    // Define units that should have no space
                    const noSpaceUnits = [
                      'g',
                      'kg',
                      'mL',
                      'L',
                      'tsp',
                      'tbsp',
                      'oz',
                      'lb',
                    ]
                    const hasNoSpace = noSpaceUnits.includes(ingredient.unit)

                    return (
                      <div key={index} className="flex items-center">
                        <span className="text-gray-800 mr-2 leading-none">
                          •
                        </span>
                        <span className="flex-1">
                          <span className="font-semibold">
                            {ingredient.item}
                          </span>
                          {ingredient.amount && (
                            <span className="text-gray-700 ml-1">
                              {ingredient.amount}
                              {ingredient.unit &&
                                (hasNoSpace
                                  ? ingredient.unit
                                  : ` ${ingredient.unit}`)}
                            </span>
                          )}
                        </span>
                      </div>
                    )
                  })
                } catch (e) {
                  // If all parsing fails, fall back to simple string split
                  return (recipe.ingredients || '')
                    .split(',')
                    .map((ingredient, index) => (
                      <div key={index} className="flex items-center">
                        <span className="text-gray-800 mr-2 leading-none">
                          •
                        </span>
                        <span className="flex-1 font-semibold">
                          {ingredient.trim()}
                        </span>
                      </div>
                    ))
                }
              }
            })()}
          </div>
        </div>
        <div className="w-full sm:w-[70%] px-2 min-w-[200px]">
          <h4 className="text-xl font-semibold text-gray-800 mb-4">
            Instructions
          </h4>
          <div className="space-y-2 text-sm sm:text-base">
            {(() => {
              try {
                // Check if instructions is already an array
                if (Array.isArray(recipe.instructions)) {
                  return recipe.instructions.map((instruction, index) => (
                    <div key={index} className="flex items-start">
                      <span className="instruction-number flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="flex-1 self-center">{instruction}</span>
                    </div>
                  ))
                }

                // Otherwise try to parse as JSON
                const parsedInstructions = JSON.parse(
                  recipe.instructions || '[]'
                )
                if (Array.isArray(parsedInstructions)) {
                  return parsedInstructions.map((instruction, index) => (
                    <div key={index} className="flex items-start">
                      <span className="instruction-number flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="flex-1 self-center">{instruction}</span>
                    </div>
                  ))
                } else {
                  return (recipe.instructions || '')
                    .split(',')
                    .map((instruction, index) => (
                      <div key={index} className="flex items-start">
                        <span className="instruction-number flex items-center justify-center flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>
                        <span className="flex-1 self-center">
                          {instruction.trim()}
                        </span>
                      </div>
                    ))
                }
              } catch (error) {
                return (recipe.instructions || '')
                  .split(',')
                  .map((instruction, index) => (
                    <div key={index} className="flex items-start">
                      <span className="instruction-number flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="flex-1 self-center">
                        {instruction.trim()}
                      </span>
                    </div>
                  ))
              }
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecipeCard
